import PropTypes from "prop-types";
import clsx from "clsx";
import { v4 as uuidv4 } from "uuid";

import { useEffect, useState, useMemo } from "react";

// React Query
import { useMutation } from "@tanstack/react-query";

import { useGridApiRef } from "@mui/x-data-grid-premium";

// Services
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// MUI
import {
	Grid,
	Typography,
	FormControlLabel,
	Menu,
	Box,
	FormControl,
	RadioGroup,
	Radio,
	Button,
	Stack,
	Checkbox,
	Divider,
} from "@mui/material";

// Context
import { useMotivators } from "@/Motivators/context/Common/MotivatorsContext";
import { useManagedSpecialUnits } from "@/Motivators/context/SpecialUnits/ManagedContext";

// Components
import InventorySearchInput from "@/Layouts/components/InputComponents/InventorySearchInput";
import ManagedUnitInput from "./ManagedUnitInput";
import SpecialUnitsManagedTable from "./SpecialUnitsManagedTable";
import MainDialogComponent from "@/Layouts/components/Dialogs/MainDialog";
import { onUpdateManagedSpecialUnitsList } from "@/Motivators/api/specialUnits/subscriptions";
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";

// Images
import emptyTable from "@/assets/images/commons/empty_table.png";

// Constants
const MONSERRAT_12 = "regular-montserrat-label fs-12";

const RenderRepeatedStocks = ({ dialogContent, checkedItems, setCheckedItems, setIsChecked, selectAllStatus, setSelectAllStatus }) => {
	useEffect(() => {
		const anyChecked = Object.values(checkedItems).some((checked) => checked);
		setIsChecked(anyChecked);
	}, [checkedItems]);

	useEffect(() => {
		setCheckedItems({});
		setSelectAllStatus({});
		setIsChecked(false);

		return () => {
			setCheckedItems({});
			setSelectAllStatus({});
			setIsChecked(false);
		};
	}, [setCheckedItems, setSelectAllStatus, setIsChecked]);

	if (!dialogContent.length) {
		return null;
	}

	const handleSelectAllChange = (groupKey, checked) => {
		setSelectAllStatus((prevState) => ({
			...prevState,
			[groupKey]: checked,
		}));

		const updatedCheckedItems = { ...checkedItems };
		dialogContent.forEach((item) => {
			const key = Object.keys(item)[0];
			if (key === groupKey) {
				const { stockIds } = item[key];
				stockIds.forEach((stockId) => {
					updatedCheckedItems[stockId] = checked;
				});
			}
		});
		setCheckedItems(updatedCheckedItems);
	};

	const handleCheckboxChange = (stockId, checked) => {
		setCheckedItems((prevState) => ({
			...prevState,
			[stockId]: checked,
		}));

		dialogContent.forEach((item) => {
			const key = Object.keys(item)[0];
			const { stockIds } = item[key];
			if (stockIds.includes(stockId)) {
				const allChecked = stockIds.every((id) => (id === stockId ? checked : checkedItems[id]));
				setSelectAllStatus((prevState) => ({
					...prevState,
					[key]: allChecked,
				}));
			}
		});
	};

	return (
		<Grid sx={{ overflowY: "auto", maxHeight: "180px", marginRight: "5px", border: "0px solid black", height: "180px" }}>
			{dialogContent?.map((item, index) => {
				const key = Object.keys(item)[0];
				const { search, stockIds } = item[key];

				return (
					<Grid key={item}>
						<Stack spacing={1} key={key} sx={{ marginLeft: "10px" }}>
							<Typography
								className={"regular-montserrat-label bold fs-14"}
								sx={{ paddingLeft: "3px", paddingBottom: "5px", paddingTop: "15px" }}
							>
								{search}
							</Typography>
							<FormControlLabel
								className={"!ml-[-10px] !mb-[-10px] !mt-[-5px]"}
								label={<Typography className={MONSERRAT_12}>Select All</Typography>}
								control={
									<Checkbox
										checked={!!selectAllStatus[key]}
										onChange={(e) => handleSelectAllChange(key, e.target.checked)}
										disableRipple
									/>
								}
							/>
							<div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
								{stockIds.map((stockId) => (
									<FormControlLabel
										className={"!ml-[-10px] !mb-[-11px] "}
										key={stockId}
										label={<Typography className={MONSERRAT_12}>{stockId}</Typography>}
										control={
											<Checkbox
												checked={!!checkedItems[stockId]}
												onChange={(e) => handleCheckboxChange(stockId, e.target.checked)}
												disableRipple
											/>
										}
										sx={{ border: "0px solid black" }}
									/>
								))}
							</div>
						</Stack>
						{index < dialogContent.length - 1 && <Divider className="border-black  mb-2.5" sx={{ marginTop: "15px" }} />}
					</Grid>
				);
			})}
		</Grid>
	);
};

const RenderButtons = ({
	dialogContent,
	checkedItems,
	setOpenDialog,
	isChecked,
	commission,
	criteria,
	response,
	query,
	addSpecialUnit,
	mutateOperation,
}) => {
	const buttonClasses = {
		"btn-regular-add": true,
		"btn-regular-text": true,
		"btn-pro-narrow": true,
	};

	const handleReplace = () => {
		const actions = [];

		dialogContent.forEach((item) => {
			const itemId = Object.keys(item)[0];
			const { stockIds, inventoryIds } = item[itemId];
			const replace = [];
			const ignore = [];

			stockIds.forEach((stockId, index) => {
				if (checkedItems[stockId]) {
					replace.push(inventoryIds[index]);
				} else {
					ignore.push(inventoryIds[index]);
				}
			});

			actions.push({
				itemId,
				replace,
				ignore,
			});
		});

		const rowsIds = response.map((row) => row._id);
		const input = { commission, criteria, rowsIds, actions, search: query.input, query: query.aggregation };
		addSpecialUnit(input, mutateOperation());
		setOpenDialog(false);
	};

	return (
		<Grid item xs={12} style={{ display: "flex", justifyContent: "center", marginBottom: "20px" }}>
			<Button
				id="select-plan-dialog-assign-btn"
				className={clsx({
					...buttonClasses,
					"!bg-gray-blue !text-white !border-gray-blue !border-[1px] !border-solid": true,
				})}
				variant="contained"
				onClick={() => {
					handleReplace();
				}}
				style={{ marginRight: "20px" }}
			>
				<Typography className="!font-sans !text-white !text-[14.5px] !font-normal !normal-case">Ignore</Typography>
			</Button>

			<Button
				id="select-plan-dialog-assign-btn"
				className={clsx({
					...buttonClasses,
				})}
				variant="contained"
				onClick={() => {
					handleReplace();
				}}
				disabled={!isChecked}
			>
				<Typography className="!font-sans !text-white !text-[14.5px] !font-normal !normal-case">Replace</Typography>
			</Button>
		</Grid>
	);
};

export default function ManagedSpecialUnitsTable(props) {
	const apiRef = useGridApiRef();
	const { isLoading, locationId, mutations, selection, setIsLoading } = useMotivators();
	const { setHidePanelResponse, searchExist, dataSearch, updateData } = useManagedSpecialUnits();
	const { data } = props;
	const [anchorEl, setAnchorEl] = useState(null);
	const [criteria, setCriteria] = useState(null);
	const [response, setResponse] = useState([]);
	const [commission, setCommission] = useState({});
	const [inputError, setInputError] = useState(true);
	const [query, setQuery] = useState(null);
	const [openDialog, setOpenDialog] = useState(false);
	const [dialogContent, setDialogContent] = useState(false);
	const [isChecked, setIsChecked] = useState(false);
	const [checkedItems, setCheckedItems] = useState({});
	const [selectAllStatus, setSelectAllStatus] = useState({});
	const { addSubscription } = useSubscriptions();

	const isMenuOpen = Boolean(anchorEl);
	// suscribe to inventory updates.
	useMemo(() => {
		if (locationId && props.commissionCycleId) {
			addSubscription(
				["managedSpecialUnits", locationId, props.commissionCycleId],
				[
					{
						context: "onUpdateManagedSpecialUnitsList",
						query: onUpdateManagedSpecialUnitsList,
						variables: {
							locationId,
							commissionCycleId: props.commissionCycleId,
						},
					},
				]
			);
		}
	}, [locationId, props.commissionCycleId, addSubscription, onUpdateManagedSpecialUnitsList]);

	//Mutate Operation
	const mutateOperation = () => ({
		onSettled: () => {
			setHidePanelResponse(true);
		},
	});

	const sanitizeId = (id) => (id?.includes("_") ? id?.split("_")[1] : id);

	const radioOptions = [
		{ value: "fixed", label: "Fixed" },
		{ value: "dynamic", label: "Dynamic" },
	];

	const repeatedStockIds = (responseStockIds) => {
		const results = {};
		responseStockIds.forEach((stockId) => {
			const row = props.data.find((rowObj) => rowObj.stockId === stockId);
			if (!row) {
				return;
			}

			const parentId = row.hierarchy[0];
			const parentRow = props.data.find((item) => item._id === parentId);
			if (!parentRow) {
				return;
			}

			const search = parentRow.search;
			if (!results[parentId]) {
				results[parentId] = { search, stockIds: [], inventoryIds: [] };
			}
			results[parentId].stockIds.push(stockId);
			results[parentId].inventoryIds.push(row._id);
		});

		return Object.keys(results).map((key) => ({ [key]: results[key] }));
	};

	const handleRadioButtonClick = (e) => {
		setCriteria(e.target.value);
		setAnchorEl(null);
		const rowsIds = response.map((row) => row._id);
		const reponseStockIds = response.map((row) => row.stockId);
		const repeatedStocks = repeatedStockIds(reponseStockIds);
		setDialogContent(repeatedStocks);

		if (repeatedStocks.length) {
			setCheckedItems({});
			setIsChecked(null);
			setOpenDialog(true);
		} else {
			setIsLoading(true);
			const input = { commission, rowsIds, search: query.input, query: query.aggregation, criteria: e.target.value };
			addSpecialUnit(input, mutateOperation());
		}
	};

	const { mutate: addSpecialUnit } = useMutation({
		mutationFn: async (input) => {
			return mutations.createItem({
				locationId,
				listId: sanitizeId(selection._id),
				commissionCycleId: selection.commissionCycleId,
				row: input,
				specialUnitsListType: selection?.specialUnitsListType,
			});
		},
	});

	const { mutate: deleteRows } = useMutation({
		mutationFn: async (rowIds) => {
			return mutations.deleteItem({
				locationId,
				listId: sanitizeId(selection._id),
				itemIds: rowIds,
				commissionCycleId: selection.commissionCycleId,
				specialUnitsListType: selection?.specialUnitsListType,
			});
		},
	});

	const handleOnChange = (obj) => {
		const { error } = obj;
		setCommission(obj.commission);
		setInputError(!!error);
	};

	const isDataInvalid = !dataSearch?.length || searchExist || !updateData;
	const isProcessing = inputError || isLoading;
	const isDisabled = isDataInvalid || isProcessing;

	return (
		<Grid container flexDirection="column">
			<Grid item sx={{ position: "relative" }}>
				<Grid container flexDirection="row">
					<Grid item>
						<InventorySearchInput setQuery={setQuery} setResponse={setResponse} dateRange={props.dateRange} rows={data ?? []} />
					</Grid>

					<Grid item className="managed-input-element">
						<ManagedUnitInput onChange={handleOnChange} dateRange={props.dateRange} />
					</Grid>

					<Grid item className="managed-input-element">
						<Button
							id="select-plan-dialog-assign-btn"
							className={clsx({
								"tier-btn": true,
								"add-items-btn": true,
							})}
							variant="contained"
							onClick={(e) => {
								setCriteria(null);
								setAnchorEl(e.currentTarget);
							}}
							disabled={isDisabled}
						>
							<Typography className="!font-sans !text-white !text-[14.5px] !font-normal !normal-case" whiteSpace={"nowrap"}>
								Add Items
							</Typography>
						</Button>
						<Menu
							anchorEl={anchorEl}
							id="select-plan-dialog-assign-btn-menu"
							open={isMenuOpen}
							onClose={() => setAnchorEl(null)}
							MenuListProps={{
								"aria-labelledby": "select-plan-dialog-assign-btn",
							}}
							className={clsx({
								"[&>.MuiPaper-root]:!w-[100px]": true,
								"[&>.MuiPaper-root]:!rounded-[5px]": true,
								"[&>.MuiPaper-root]:!pb-3": true,
								"[&>.MuiPaper-root>ul]:!p-0": true,
							})}
						>
							<Box ml={1} className="!m-0 !px-3">
								<FormControl>
									<RadioGroup
										value={criteria}
										onChange={handleRadioButtonClick}
										aria-labelledby="demo-radio-buttons-group-label"
										name="radio-buttons-group"
									>
										{radioOptions.map((option, index) => (
											<FormControlLabel
												key={uuidv4()}
												className={clsx({
													"!-mb-[10px]": index === 0,
												})}
												value={option.value}
												control={
													<Radio className="!text-primary [&.Mui-checked]:!text-primary [&_.MuiSvgIcon-root]:!text-[16px]" />
												}
												label={
													<Typography className="!font-sans !text-[14.5px] !font-normal !text-default">
														{option.label}
													</Typography>
												}
											/>
										))}
									</RadioGroup>
								</FormControl>
							</Box>
						</Menu>
					</Grid>
				</Grid>
				{data.length === 0 && (
					<Grid justifyContent="center" alignItems="center">
						<EmptyBody
							image={emptyTable}
							sx={{
								height: "400px",
								boxShadow: "none !important",
								width: "400px",
								marginTop: "32px",
								marginLeft: "auto",
								marginRight: "auto",
							}}
						/>
					</Grid>
				)}
				<Grid className="w-[1090px] mt-3 z-[1]">
					<SpecialUnitsManagedTable
						commissionCycleId={props.commissionCycleId}
						listId={props.currentListId}
						rows={data ?? []}
						apiRef={apiRef}
						deleteRows={deleteRows}
						locationId={props.locationId}
						motivatorType={props.motivatorType}
						query={query?.input}
					/>
				</Grid>
				<MainDialogComponent
					openDialog={openDialog}
					onClose={() => {
						setOpenDialog(false);
						setIsChecked(null);
					}}
					width={400}
					modalTitle={"The following stock number(s) are already contained in special units lists"}
					titleClasses={MONSERRAT_12}
					headerStyle={{ paddingLeft: "60px", width: "400px" }}
				>
					<Grid container spacing={2} className={`auto-alerts-grid-container !w-full !ml-0 mt-2`}>
						<Grid item xs={12} sx={{ height: "180px" }}>
							<RenderRepeatedStocks
								dialogContent={dialogContent}
								checkedItems={checkedItems}
								setCheckedItems={setCheckedItems}
								setIsChecked={setIsChecked}
								selectAllStatus={selectAllStatus}
								setSelectAllStatus={setSelectAllStatus}
							/>
						</Grid>
						<RenderButtons
							dialogContent={dialogContent}
							checkedItems={checkedItems}
							setOpenDialog={setOpenDialog}
							isChecked={isChecked}
							commission={commission}
							criteria={criteria}
							response={response}
							query={query}
							addSpecialUnit={addSpecialUnit}
							mutateOperation={mutateOperation}
						/>
					</Grid>
				</MainDialogComponent>
			</Grid>
		</Grid>
	);
}

ManagedSpecialUnitsTable.propTypes = {
	data: PropTypes.array.isRequired,
	dateRange: PropTypes.object.isRequired,
	commissionCycleId: PropTypes.string.isRequired,
	currentListId: PropTypes.string.isRequired,
	locationId: PropTypes.string.isRequired,
	motivatorType: PropTypes.string.isRequired,
};

RenderButtons.propTypes = {
	dialogContent: PropTypes.array.isRequired,
	checkedItems: PropTypes.object.isRequired,
	setOpenDialog: PropTypes.func.isRequired,
	isChecked: PropTypes.bool.isRequired,
	commission: PropTypes.object.isRequired,
	criteria: PropTypes.string,
	response: PropTypes.array.isRequired,
	query: PropTypes.string,
	addSpecialUnit: PropTypes.func.isRequired,
	mutateOperation: PropTypes.func.isRequired,
};

RenderRepeatedStocks.propTypes = {
	dialogContent: PropTypes.arrayOf(PropTypes.object),
	checkedItems: PropTypes.object.isRequired,
	setCheckedItems: PropTypes.func.isRequired,
	setIsChecked: PropTypes.func.isRequired,
	selectAllStatus: PropTypes.object.isRequired,
	setSelectAllStatus: PropTypes.func.isRequired,
};
